import axios from 'axios';

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.otoflow.caliskanservis.com/api';

export const ASSET_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

export const resolveAssetUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${ASSET_BASE_URL}${url}`;
  return `${ASSET_BASE_URL}/${url}`;
};

const dataUrlToObjectUrl = (dataUrl) => {
  try {
    if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return '';
    const cached = dataUrlCache.get(dataUrl);
    if (cached) return cached;
    const match = dataUrl.match(/^data:([^;]+);base64,(.*)$/);
    if (!match) return '';
    const mime = match[1];
    const b64 = match[2];
    const binary = atob(b64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime || 'image/jpeg' });
    const url = URL.createObjectURL(blob);
    dataUrlCache.set(dataUrl, url);
    return url;
  } catch {
    return '';
  }
};

const dataUrlCache = new Map();

const bufferToDataUrl = (bufObj) => {
  try {
    if (!bufObj || typeof bufObj !== 'object') return '';
    const dataArr = bufObj.data || bufObj;
    if (!Array.isArray(dataArr)) return '';
    const bytes = new Uint8Array(dataArr);
    // Try to interpret as UTF-8 text (data URL stored as text blob)
    if (typeof TextDecoder !== 'undefined') {
      const text = new TextDecoder().decode(bytes);
      if (text.startsWith('data:')) return text;
      if (text.startsWith('/9j/') || text.startsWith('iVBORw0') || text.startsWith('JVBERi0')) {
        return normalizeFileUrl(text);
      }
    }
    // Fallback: assume image/jpeg binary and base64 it
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    const b64 = btoa(binary);
    return `data:image/jpeg;base64,${b64}`;
  } catch {
    return '';
  }
};

// Normalize stored file strings (data URL, absolute URL, or raw base64)
export const normalizeFileUrl = (val) => {
  if (!val) return '';
  if (typeof val === 'object') {
    if (val.type === 'Buffer') return bufferToDataUrl(val);
    return bufferToDataUrl(val);
  }
  if (typeof val !== 'string') return '';
  if (val.startsWith('data:')) {
    const cleaned = val.replace(/\s/g, '');
    // Prefer blob URLs to avoid ERR_INVALID_URL on large data URLs
    const objUrl = dataUrlToObjectUrl(cleaned);
    return objUrl || cleaned;
  }
  if (val.startsWith('http://') || val.startsWith('https://') || val.startsWith('/')) {
    return resolveAssetUrl(val);
  }
  // Heuristics for common base64 signatures
  if (val.startsWith('JVBERi0')) return `data:application/pdf;base64,${val}`;
  if (val.startsWith('/9j/')) return `data:image/jpeg;base64,${val}`;
  if (val.startsWith('iVBORw0')) return `data:image/png;base64,${val}`;
  return `data:application/octet-stream;base64,${val}`;
};

// ── Frontend API Cache ─────────────────────────────────
// Sık değişmeyen veriler için hafıza önbelleği
const apiCache = new Map();
const CACHE_TTL = {
  '/roller':    5 * 60 * 1000,  // 5 dakika
  '/ayarlar':   5 * 60 * 1000,  // 5 dakika
  '/kullanicilar': 2 * 60 * 1000, // 2 dakika
  '/tedarikci/admin': 3 * 60 * 1000, // 3 dakika
};

const getCacheTTL = (url) => {
  for (const [key, ttl] of Object.entries(CACHE_TTL)) {
    if (url.includes(key)) return ttl;
  }
  return 0; // Cache yok
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/* Token varsa her isteğe ekle */
const token = localStorage.getItem('otoflow_token');
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('otoflow_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;

  // GET cache kontrolü
  if (cfg.method === 'get' || cfg.method === 'GET') {
    const ttl = getCacheTTL(cfg.url || '');
    if (ttl > 0) {
      const cacheKey = cfg.url;
      const cached = apiCache.get(cacheKey);
      if (cached && Date.now() - cached.time < ttl) {
        cfg._fromCache = cached.data;
      }
    }
  }
  return cfg;
});

/* 401 → otomatik çıkış */
api.interceptors.response.use(
  res => {
    // Cache'e kaydet
    if (res.config.method === 'get') {
      const ttl = getCacheTTL(res.config.url || '');
      if (ttl > 0) {
        apiCache.set(res.config.url, { data: res, time: Date.now() });
      }
    }
    return res;
  },
  err => {
    if (err.response?.status === 401 && !String(err.config?.url || '').startsWith('/auth/login')) {
      localStorage.removeItem('otoflow_token');
      localStorage.removeItem('otoflow_kullanici');
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

// Cache temizle (yazma işlemlerinde çağrılabilir)
export const clearApiCache = (pattern) => {
  if (pattern) {
    for (const key of apiCache.keys()) {
      if (key.includes(pattern)) apiCache.delete(key);
    }
  } else {
    apiCache.clear();
  }
};

export default api;
